import { ChevronDown, Plus } from "lucide-react-native";
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ComponentRef,
} from "react";

import { useKanbanColors } from "@/features/player-kanban/hooks/useKanbanColors";
import BoardDropdownItem from "@/features/player-kanban/components/organisms/BoardDropdownItem";
import BoardOptionsMenu from "@/features/player-kanban/components/molecules/BoardOptionsMenu/BoardOptionsMenuContainer";
import BoardRoleBadge from "@/features/player-kanban/components/molecules/BoardRoleBadge";
import BoardTab from "@/features/player-kanban/components/molecules/BoardTab";

import { Box } from "@/components/atoms/Box";
import { Icon } from "@/components/atoms/Icon";
import { Stack } from "@/components/atoms/Stack";
import { Pressable } from "@/components/atoms/Pressable";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Text } from "@/components/atoms/Text";

import { TEST_ID_PLAYER_KANBAN } from "e2e/testId";
import { UserKanbanBoardRole } from "@/generated/graphql";

/**
 * Interface for board information displayed in tabs
 */
export interface BoardInfo {
  /** Unique board identifier */
  id: string;
  /** Display name of the board */
  name: string;
  /** Whether this board is currently active */
  isActive: boolean;
  /** Current user's role on this board */
  userRole?: UserKanbanBoardRole | null;
}

/**
 * Props for BoardTabs component
 */
interface BoardTabsProps {
  /** Array of boards to display as tabs */
  boards: BoardInfo[];
  /** Callback when user selects a board tab */
  onBoardSelect: (boardId: string) => void;
  /** Callback when user clicks create new board */
  onCreateBoard: () => void;
  /** Callback when user clicks more options menu */
  onMoreOptions: () => void;
  /** Callback when user selects rename board option */
  onRenameBoard?: () => void;
  /** Callback when user selects delete board option */
  onDeleteBoard?: () => void;
  /** Whether user can edit the board (hide options menu for viewers) */
  canEdit?: boolean;
  /** Current user's role on the active board */
  userRole?: UserKanbanBoardRole | null;
  /**
   * SE-6575: whether boards exist that could not be loaded (the board-list page
   * walk hit its cap or a page request failed). When true the count reads as a
   * lower bound so the list is never silently truncated.
   */
  hasUnreachableBoards?: boolean;
}

/** Maximum number of boards to show as tabs before switching to dropdown */
const MAX_VISIBLE_TABS = 5;

/**
 * BoardTabs Component - Scrollable board tabs with create and options
 *
 * Displays board tabs in a horizontally scrollable view with:
 * - Active board highlighting with blue border
 * - Auto-scroll to active board
 * - Dropdown selector when more than 5 boards exist
 * - Create new board tab
 * - Options menu for board management (when multiple boards exist)
 * @param root0 - The component props
 * @param root0.boards - Array of board information objects to display as tabs
 * @param root0.onBoardSelect - Callback invoked when a board tab is selected
 * @param root0.onCreateBoard - Callback invoked when the create new board button is pressed
 * @param root0.onMoreOptions - Callback invoked when the board options menu is opened
 * @param root0.onRenameBoard - Optional callback for handling board rename action
 * @param root0.onDeleteBoard - Optional callback for handling board deletion action
 * @param root0.canEdit - Boolean flag indicating if the user has edit permissions for board management options
 * @param root0.userRole - Current user's role on the active board for display in dropdown
 * @param root0.hasUnreachableBoards - Whether boards exist beyond those loaded (SE-6575)
 */
const BoardTabsContainer = ({
  boards,
  onBoardSelect,
  onCreateBoard,
  onMoreOptions: _onMoreOptions,
  onRenameBoard,
  onDeleteBoard,
  canEdit = true,
  userRole,
  hasUnreachableBoards = false,
}: BoardTabsProps) => {
  const colors = useKanbanColors();
  const scrollViewRef = useRef<ComponentRef<typeof ScrollView>>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Determine if we should show dropdown mode
  const useDropdownMode = boards.length > MAX_VISIBLE_TABS;
  const activeBoard = boards.find(b => b.isActive);

  // Calculate if delete should be disabled (prevent deletion of last board)
  const canDelete = boards.length > 1;

  const handleBoardPress = useCallback(
    (boardId: string) => {
      onBoardSelect(boardId);
      setIsDropdownOpen(false);
    },
    [onBoardSelect]
  );

  const handleRenameBoard = useCallback(() => {
    onRenameBoard?.();
  }, [onRenameBoard]);

  const handleDeleteBoard = useCallback(() => {
    onDeleteBoard?.();
  }, [onDeleteBoard]);

  const toggleDropdown = useCallback(() => {
    setIsDropdownOpen(prev => !prev);
  }, []);

  const renderTab = useCallback(
    (board: BoardInfo) => (
      <BoardTab
        key={board.id}
        boardId={board.id}
        name={board.name}
        isActive={board.isActive}
        onBoardSelect={handleBoardPress}
      />
    ),
    [handleBoardPress]
  );

  // Auto-scroll to active board when it changes (only in tab mode)
  useEffect(() => {
    if (useDropdownMode) return;
    const activeIndex = boards.findIndex(board => board.isActive);
    if (activeIndex !== -1 && scrollViewRef.current) {
      const scrollPosition = activeIndex * 130;
      scrollViewRef.current?.scrollTo({ x: scrollPosition, animated: true });
    }
  }, [boards, useDropdownMode]);

  // Dropdown mode - show selector with active board name
  if (useDropdownMode) {
    return (
      <Stack
        direction="horizontal"
        className="grow items-center gap-2 overflow-visible px-2"
      >
        {/* Board Selector Dropdown */}
        <Box className="relative z-50 overflow-visible">
          <Pressable
            onPress={toggleDropdown}
            testID={TEST_ID_PLAYER_KANBAN.BOARD_DROPDOWN_TOGGLE}
            className="h-10 min-w-48 flex-row items-center justify-between gap-2 rounded-sm border border-outline-subtle bg-surface-base px-4 py-2"
          >
            <Stack direction="horizontal" className="flex-1 items-center gap-2">
              <Text
                variant="body-strong"
                className="text-content-primary"
                isTruncated
              >
                {activeBoard?.name || "Select Board"}
              </Text>
              {userRole != null && <BoardRoleBadge role={userRole} />}
            </Stack>
            <Icon as={ChevronDown} size="md" color={colors.textSecondary} />
          </Pressable>

          {/* Dropdown Menu */}
          {isDropdownOpen && (
            <Box className="absolute left-0 top-12 z-50 min-w-56 overflow-visible rounded-sm border border-outline-subtle bg-surface-base shadow-hard-2">
              <ScrollView showsVerticalScrollIndicator className="max-h-64">
                <Stack className="py-1">
                  {boards.map(board => (
                    <BoardDropdownItem
                      key={board.id}
                      boardId={board.id}
                      name={board.name}
                      isActive={board.isActive}
                      userRole={board.userRole}
                      onBoardSelect={handleBoardPress}
                    />
                  ))}
                </Stack>
              </ScrollView>
            </Box>
          )}
        </Box>

        {/* Board count indicator. SE-6575: when the board-list walk could not
            reach every board, the count reads as a lower bound and says so,
            rather than presenting a truncated list as the whole set. */}
        <Text
          variant="caption"
          className="text-content-muted"
          testID={TEST_ID_PLAYER_KANBAN.BOARD_COUNT_INDICATOR}
        >
          {hasUnreachableBoards
            ? `${boards.length}+ boards (more not loaded)`
            : `${boards.length} boards`}
        </Text>

        {/* Create New Board Button */}
        <Pressable
          testID={TEST_ID_PLAYER_KANBAN.CREATE_BOARD_BUTTON}
          accessibilityRole="button"
          accessibilityLabel="Create new board"
          onPress={onCreateBoard}
          className="h-10 items-center justify-center rounded-sm border border-dashed border-outline-subtle bg-transparent px-3 py-2"
        >
          <Stack direction="horizontal" className="items-center gap-1">
            <Icon as={Plus} size="md" color={colors.textSecondary} />
            <Text
              variant="unstyled"
              className="font-medium text-caption text-content-muted"
            >
              New
            </Text>
          </Stack>
        </Pressable>

        {/* More Options - Show when user can edit */}
        {canEdit && (
          <BoardOptionsMenu
            onRenameBoard={handleRenameBoard}
            onDeleteBoard={handleDeleteBoard}
            canDelete={canDelete}
          />
        )}
      </Stack>
    );
  }

  // Tab mode - original horizontal scrolling tabs
  return (
    <ScrollView
      ref={scrollViewRef}
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={{
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 4,
        paddingHorizontal: 8,
        minWidth: "100%",
      }}
      className="grow"
    >
      {/* All Board Tabs */}
      {boards.map(board => renderTab(board))}

      {/* Create New Board Tab */}
      <Pressable
        testID={TEST_ID_PLAYER_KANBAN.CREATE_BOARD_BUTTON}
        accessibilityRole="button"
        accessibilityLabel="Create new board"
        onPress={onCreateBoard}
        className="h-10 min-w-28 items-center justify-center rounded-t-sm border border-b-0 border-dashed border-outline-subtle bg-transparent px-3 py-2"
      >
        <Stack direction="horizontal" className="items-center gap-1">
          <Icon as={Plus} size="md" color={colors.textSecondary} />
          <Text
            variant="unstyled"
            className="font-medium text-caption text-content-muted"
          >
            New
          </Text>
        </Stack>
      </Pressable>

      {/* More Options Dropdown - Show when there are multiple boards and user can edit */}
      {boards.length > 1 && canEdit && (
        <BoardOptionsMenu
          onRenameBoard={handleRenameBoard}
          onDeleteBoard={handleDeleteBoard}
          canDelete={canDelete}
        />
      )}
    </ScrollView>
  );
};

export default BoardTabsContainer;
